import { AfterViewInit, Directive, ElementRef, inject, OnDestroy, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { FormGroup } from "@angular/forms";
import { SearchedInfo } from "../../model/searchedinfo";
import { LastSearchService } from "../../services/internal-services/lastsearch.service";
import { GeoService } from "../../services/internal-services/geo.service";
import { ApiViewerService } from "../../services/internal-services/api-viewer.service";
import { IMetadataImage } from "../../interfaces/i-metadataimage";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { MapService } from "../../services/internal-services/map.service";
import { VIEWER_SERVICE } from "../../services/internal-services/viewer-services/inject-base-viewer.service";

@Directive()
export abstract class SearchManagerComponent implements AfterViewInit, OnDestroy {
  viewerService = inject(VIEWER_SERVICE);

  @ViewChild("addressInput", { static: true }) addressEl: ElementRef;

  form: FormGroup;

  focus: boolean;

  geocoder: google.maps.Geocoder;

  subscriptions: Subscription[] = [];

  lastSearchedInfo: SearchedInfo[] = [];

  selectedImage: IMetadataImage;

  previousImage: IMetadataImage;

  previousSearch: SearchedInfo;

  protected constructor(
    protected mapService: MapService,
    protected lastSearchService: LastSearchService,
    protected apiViewerService: ApiViewerService,
    public dialogGallery: MatDialog,
  ) {}

  ngAfterViewInit() {
    this.subscriptions.push(
      this.lastSearchService.lastSearchs$.subscribe((lastSearchs: SearchedInfo[]) => {
        this.lastSearchedInfo = lastSearchs;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  searchAddress(location) {
    if (location) {
      const options = { address: location.replace(/[()]/g, "") };
      GeoService.geocode(options).subscribe((results: google.maps.GeocoderResult[]) => {
        const firstResult: google.maps.GeocoderResult = results[0];
        const searchedInfo = new SearchedInfo().fromGeocoderResult(firstResult);
        let latLong;
        if (location instanceof google.maps.LatLng || GeoService.isCoordinates(location)) {
          const coordinates = GeoService.parseCoordinates(location);
          latLong = {
            lat: Number(coordinates[0]),
            lng: Number(coordinates[1]),
          };
          searchedInfo.location = new google.maps.LatLng(latLong.lat, latLong.lng);
          searchedInfo.formattedAddress = null;
        }

        if (this.isAddressOrCoordinates(searchedInfo)) {
          this.viewerService.setZoom(ViewerConstants.DEFAULT_SEARCH_ZOOM);
        }

        this.handleSearchInfo(searchedInfo);
      });
    }
  }

  isAddressOrCoordinates(searchedInfo: SearchedInfo): boolean {
    // if formatted address includes digits, then it is a street address (and not a city)
    const isAddress = /\d/.test(searchedInfo.formattedAddress);
    let isCoordinates;
    if (searchedInfo.location.lat() && !searchedInfo.formattedAddress) {
      isCoordinates = true;
    } else {
      isCoordinates = false;
    }
    return isAddress || isCoordinates;
  }

  /**
   * Handle search info after reverse geocode
   * If discrete image is enabled, save data and wait the new image metadata
   * If ortho mode is enabled, get visible layers, images for this layer, and after that
   * get new layer with coverage on top of others and get the image
   * @param searchedInfo
   */
  abstract handleSearchInfo(searchedInfo: SearchedInfo): void;

  abstract setAddressResultToMap(searchedInfo: SearchedInfo): void;

  abstract handleDiscreteSearch(searchedInfo: SearchedInfo): void;

  getExtentPolygonFromResult(searchedInfo: SearchedInfo): any {
    if (searchedInfo && searchedInfo.viewport) {
      const viewportJson = searchedInfo.viewport.toJSON();
      return [viewportJson.west, viewportJson.south, viewportJson.east, viewportJson.north];
    }
    return null;
  }

  showLocationInfo(searchedInfo: SearchedInfo) {
    const featureMercator = GeoService.pointToMercator([searchedInfo.location.lng(), searchedInfo.location.lat()]);
    const pixel = this.viewerService.coordinatesToPixel([featureMercator.geometry.coordinates]);
    const features = this.mapService.getCoreMap().getFeaturesClicked(pixel[0], featureMercator.geometry.coordinates);
    this.viewerService.showLocationInfo(
      [searchedInfo.location.lng(), searchedInfo.location.lat()],
      features.length ? features : null,
    );
  }
}
